import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Bell } from "lucide-react";
import { Button } from "./ui/button";
import { ThemeToggle } from "./theme-toggle";
import baseLogo from "../images/base-logo.svg";

type MainTab = "bounties" | "projects" | "leaderboard";

interface NavbarProps {
  mainTab: MainTab;
  setMainTab: (tab: MainTab) => void;
}

export default function Navbar({ mainTab, setMainTab }: NavbarProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Always show tabs except on login page
  const showTabs = location.pathname !== "/login";

  return (
    <header className="bg-[#110972] border-b px-20 py-5 flex items-center justify-between relative shadow-sm">
      <div className="flex items-center min-w-[48px]">
        {/* Logo */}
        <div className="w-12 h-12 flex items-center justify-center mr-12 cursor-pointer" onClick={() => navigate("/")}> 
          <img src={baseLogo} alt="Base Logo" className="w-full h-full" />
        </div>
        
        {showTabs && (
          <div className="flex gap-6 ml-8">
            <Link
              to="/bounties"
              className={`${
                mainTab === "bounties"
                  ? "text-[#FAF94F] border-[#FAF94F]"
                  : "text-[#A1AAFF] border-transparent"
              } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              onClick={() => setMainTab("bounties")}
            >
              Bounties
            </Link>
            <Link
              to="/projects"
              className={`${
                mainTab === "projects"
                  ? "text-[#FAF94F] border-[#FAF94F]"
                  : "text-[#A1AAFF] border-transparent"
              } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              onClick={() => setMainTab("projects")}
            >
              Projects
            </Link>
            <Link
              to="/leaderboard"
              className={`${
                mainTab === "leaderboard"
                  ? "text-[#FAF94F] border-[#FAF94F]"
                  : "text-[#A1AAFF] border-transparent"
              } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              onClick={() => setMainTab("leaderboard")}
            >
              Leaderboard
            </Link>
          </div>
        )}
      </div>
      
      <div className="flex items-center gap-4">
        {/* Notification (only if logged in) */}
        {user && (
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center relative">
            <Bell className="w-6 h-6 text-white/70" />
            <div className="absolute top-2 right-2 w-2 h-2 bg-[#FF5629] rounded-full" />
          </div>
        )}
        {/* Profile or Login */}
        {user ? (
          <div 
            className="py-2 px-4 bg-[#FAF94F] rounded-full flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/profile')}
          >
            <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-black">
              {user.photoURL ? (
                <img 
                  src={user.photoURL} 
                  alt="profile" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    console.error('Error loading profile image:', e);
                    e.currentTarget.src = "https://placehold.co/32x32";
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <span className="text-sm font-bold text-gray-500">
                    {user.displayName?.[0]?.toUpperCase() || 'U'}
                  </span>
                </div>
              )}                                                                    
            </div>
            <div className="text-black text-base font-['OT_Bulb_Classic'] font-medium">
              @{user.displayName?.split(' ')[0].toLowerCase() || 'user'}
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-4">
            <Link
              to="/login"
              className="text-white hover:text-[#FAF94F]"
            >
              Login
            </Link>
            <Link
              to="/register"
              className="bg-[#FAF94F] text-black px-4 py-2 rounded-full text-sm font-medium hover:bg-[#FAF94F]/90"
            >
              Register
            </Link>
          </div>
        )}
        {/* Theme Toggle */}
        <ThemeToggle />
        {/* Logout Button (only if logged in) */}
        {user && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="ml-2 text-white border-white hover:bg-white/10"
          >
            Logout
          </Button>
        )}
      </div>
    </header>
  );
} 